// "Moved yesterday" helpers (brief v2.3 §D–§G): $ per compare base, ranking, deterministic hints, and the item list
// (partner events from `change_groups` + single movers from `changes` minus the tags an event already covers).
import {isNilValue, fmtSignedPct, fmtMoneyShort, shortProvider, driverPhrase, fmtDay, EMPTY} from "./format";
import dayjs from "dayjs";

export const MOVED_ANCHOR_ID = "tc-moved";
export const MOVED_VISIBLE = 5;
export const CHRONIC_DAYS = 4;
const EVENT_MIN = 3; // an event keeps its line while ≥ 3 of its tags survive the page filters

const COV_DROP = -15;   // pts
const RPM_DROP = -15;   // %
const SENT_DROP = -15;  // %
const SENT_FLAT = -5;   // % — "traffic flat or up"

/** Signed $ of a mover for the chosen baseline: the API's `delta_usd` (vs 7-day avg) or `rev − rev_d2` (vs day before). */
export const movedUsd = (c, compareBase) => {
    if (!c) return null;
    if (compareBase === "d1") return isNilValue(c.rev) || isNilValue(c.rev_d2) ? null : Number(c.rev) - Number(c.rev_d2);
    return isNilValue(c.delta_usd) ? null : Number(c.delta_usd);
};

/** Δ% of searches sent vs the day before; null when unknown. */
export const sentDeltaPct = (c) => (!c || isNilValue(c.fs) || isNilValue(c.fs_d2) || Number(c.fs_d2) <= 0
    ? null : ((Number(c.fs) - Number(c.fs_d2)) / Number(c.fs_d2)) * 100);

/** Filtering signature (§D rank 0): coverage ≤ −15 pts while traffic is flat or up (fs ≥ fs_d2 × 0.95) — whatever the sign of $. */
export const isFilteringSignature = (c) => Boolean(c) && !isNilValue(c.coverage_delta_pct) && Number(c.coverage_delta_pct) <= COV_DROP
    && !isNilValue(c.fs) && !isNilValue(c.fs_d2) && Number(c.fs) >= Number(c.fs_d2) * 0.95;

export const isChronic = (c) => Boolean(c) && Number(c.run_days) >= CHRONIC_DAYS;

/**
 * Rank of a single mover (§D): 0 filtering signature · 1 negative $ · 2 positive $ · 3 chronic (≥ 4 days out of band —
 * already known, listed last). Ties break on |$| in `buildMovedItems`.
 */
export const moverRank = (c, usd) => {
    if (isFilteringSignature(c)) return 0;
    if (isChronic(c)) return 3;
    return usd !== null && usd < 0 ? 1 : 2;
};

/** Partner events take the rank of their direction / metric: coverage down = 0; rpm / rev down = 1; up = 2. */
export const eventRank = (g) => (g.direction === "down" ? (g.metric === "coverage" ? 0 : 1) : 2);

const METRIC_WORD = {rpm: "pays", coverage: "ad coverage", rev: "revenue"};

/** `pays +22% per 1k on all 8` / `ad coverage −20% on all 5` / `revenue −18% on all 3`. */
export const eventDriverPhrase = (g) => {
    const raw = isNilValue(g.avg_pct) ? null : Math.abs(Number(g.avg_pct));
    const signed = raw === null ? null : g.direction === "down" ? -raw : raw;
    const word = METRIC_WORD[g.metric] || g.metric;
    return `${word} ${signed === null ? EMPTY : fmtSignedPct(signed)}${g.metric === "rpm" ? " per 1k" : ""} on all ${g.n}`;
};

/** Driver phrase of a tag from its `yesterday` block: `moved_by` when the API set it, else the raw metric moves. */
export const rowDriverPhrase = (y) => {
    if (!y) return "";
    const by = Array.isArray(y.moved_by) && y.moved_by.length ? y.moved_by : Array.isArray(y.moved_by_raw) ? y.moved_by_raw : [];
    const phrase = driverPhrase({...y, moved_by: by});
    if (phrase) return phrase;
    if (by.includes("rpm") && !isNilValue(y.rpm_delta_pct)) return `pays ${fmtSignedPct(y.rpm_delta_pct)} per 1k`;
    if (by.includes("coverage") && !isNilValue(y.coverage_delta_pct)) return `ad coverage ${fmtSignedPct(y.coverage_delta_pct)}`;
    return isNilValue(y.delta_base_pct) ? "" : `${fmtSignedPct(y.delta_base_pct)} vs 7d avg`;
};

/** ` · day 3` / ` · moved 5 of last 7 days` (chronic) — appended to the hint when `run_days ≥ 2`. */
export const runSuffix = (runDays) => {
    const n = Number(runDays) || 0;
    if (n < 2) return "";
    if (n >= CHRONIC_DAYS) return n <= 7 ? ` · moved ${n} of last 7 days` : ` · moved ${n} days in a row`;
    return ` · day ${n}`;
};

/**
 * Deterministic hint (§E table). `item` is a single mover (the `changes` row, or a `yesterday` block + `provider`) or a
 * partner event (`kind: "event"`). `ctx.attentionByProvider` = Map partner → number of its tags in Needs attention.
 * Returns `{text, short}`; `short` is the ≤ 20-char tail for the phone card (`filtering?`, `repricing`, `Tiger?`).
 * `withRun` appends the run-days suffix (the drawer prints its own).
 */
export const hintFor = (item, ctx = {}, withRun = true) => {
    if (!item) return {text: "", short: ""};
    const partner = shortProvider(item.provider);
    const attn = ctx.attentionByProvider && item.provider ? ctx.attentionByProvider.get(item.provider) || 0 : 0;
    let text = "";
    let short = "";
    if (item.kind === "event") {
        if (item.direction === "down") {
            if (item.metric === "coverage") { text = `partner counts fewer of our searches across ${partner} — watch coverage today`; short = "filtering?"; }
            else if (item.metric === "rpm") { text = `repricing across ${partner}`; short = "repricing"; }
            else { text = `revenue down across ${partner} — partner-wide?`; short = "partner-wide?"; }
        } else {
            text = `partner paying more across ${partner}`; short = "partner paying more";
        }
    } else {
        const cov = isNilValue(item.coverage_delta_pct) ? null : Number(item.coverage_delta_pct);
        const rpm = isNilValue(item.rpm_delta_pct) ? null : Number(item.rpm_delta_pct);
        const sent = sentDeltaPct(item);
        const usd = item.usd !== undefined ? item.usd : movedUsd(item, ctx.compareBase);
        const drift = Array.isArray(item.moved_by) && item.moved_by.includes("drift");
        if (cov !== null && cov <= COV_DROP && (sent === null || sent >= SENT_FLAT)) {
            text = "partner counts fewer of our searches while we send more — watch coverage today"; short = "filtering?";
        } else if (drift) {
            text = "slow slide — no single bad day, the level moved"; short = "sliding";
        } else if (rpm !== null && rpm <= RPM_DROP) {
            text = `pays less per 1k — compare sibling tags of ${partner}`; short = "repricing?";
        } else if (sent !== null && sent <= SENT_DROP) {
            text = "less traffic from Tiger — our side (routing / rotator)"; short = "Tiger?";
        } else if (attn >= 2 && usd !== null && usd < 0) {
            // Only for a loss: a gain next to partner alarms is not evidence of a partner-wide problem.
            text = `${attn} ${partner} tags already in Needs attention — partner-wide?`; short = "partner-wide?";
        }
    }
    if (withRun) text += runSuffix(item.run_days);
    return {text: text.replace(/^ · /, ""), short};
};

/** Sum the daily revenue of `rows` day by day (for an event's bars). */
export const sumDaily = (rows) => {
    const base = rows.find((r) => Array.isArray(r.daily) && r.daily.length);
    if (!base) return [];
    return base.daily.map((d, i) => ({day: d.day, rev: rows.reduce((s, r) => s + ((r.daily && r.daily[i] && Number(r.daily[i].rev)) || 0), 0)}));
};

/** `usually $22/d → $30` (7-day median; `(3 d)` when the base has fewer than 5 earning days) / `Sep 12 $22 → $30` (day before). */
export const usualLine = (ref, last, compareBase, asOf, usualN = null) => {
    const refText = isNilValue(ref) ? EMPTY : fmtMoneyShort(ref);
    const thin = !isNilValue(usualN) && Number(usualN) < 5 ? ` (${Number(usualN)} d)` : "";
    const lead = compareBase === "d1" ? `${asOf ? fmtDay(dayjs.utc(asOf).subtract(1, "day").format("YYYY-MM-DD")) : "day before"} ${refText}` : `usually ${refText}/d${thin}`;
    return {lead, last: fmtMoneyShort(last)};
};

const rowItem = (c, row, compareBase, ctx) => {
    const usd = movedUsd(c, compareBase);
    const item = {
        kind: "row", key: c.tag, tag: c.tag, c, row: row || null,
        site: c.site, provider: c.provider, network: c.type_label || c.group, prefix: c.prefix,
        usd, rev: isNilValue(c.rev) ? null : Number(c.rev), ref: compareBase === "d1" ? c.rev_d2 : c.base_rev,
        daily: Array.isArray(c.daily) && c.daily.length ? c.daily : (row && row.daily) || [],
        partialLast: Boolean(row && row.partial_last_day), runDays: Number(c.run_days) || 0, usualN: isNilValue(c.usual_n) ? null : Number(c.usual_n),
        rebased: Boolean(c.rebased), decompSignificant: c.decomp_significant !== false,
        rank: moverRank(c, usd), driver: rowDriverPhrase(c),
    };
    item.hint = hintFor({...c, usd}, ctx);
    return item;
};

/**
 * Display items (§D): partner events (from `change_groups`, restricted to the rows the filters keep) + single movers
 * (`changes` minus the tags a shown event covers). Sorted by rank, then |$|. `$` follows `compareBase`.
 * `rows` = the filtered page rows (their `yesterday` blocks feed the event members); `attention` = the alarm rows.
 */
export const buildMovedItems = ({changes, changeGroups, rows, attention, compareBase}) => {
    const byTag = new Map((rows || []).map((r) => [r.tag, r]));
    const attentionByProvider = new Map();
    (attention || []).forEach((r) => { if (r.provider) attentionByProvider.set(r.provider, (attentionByProvider.get(r.provider) || 0) + 1); });
    const ctx = {attentionByProvider, compareBase};
    const covered = new Set();
    const events = [];
    (Array.isArray(changeGroups) ? changeGroups : []).forEach((g) => {
        const tags = (Array.isArray(g.tags) ? g.tags : []).filter((t) => byTag.has(t));
        if (tags.length < EVENT_MIN) return;
        const members = tags.map((t) => byTag.get(t));
        const memberItems = members.map((r) => rowItem({...(r.yesterday || {}), tag: r.tag, site: r.site, provider: r.provider, group: r.group, type_label: r.type_label, prefix: r.prefix}, r, compareBase, ctx));
        const full = tags.length === g.tags.length;
        const usd = compareBase !== "d1" && full && !isNilValue(g.delta_usd) ? Number(g.delta_usd) : memberItems.reduce((s, m) => s + (m.usd || 0), 0);
        const item = {
            kind: "event", key: `event:${g.provider}:${g.metric}:${g.direction}`, provider: g.provider, network: members[0].type_label, prefix: members[0].prefix,
            metric: g.metric, direction: g.direction, n: tags.length, tags, members: memberItems, avg_pct: g.avg_pct,
            usd, rev: memberItems.reduce((s, m) => s + (m.rev || 0), 0), ref: memberItems.reduce((s, m) => s + (Number(m.ref) || 0), 0),
            daily: sumDaily(members), partialLast: members.some((r) => r.partial_last_day), rank: eventRank(g), runDays: 0,
        };
        item.driver = eventDriverPhrase(item);
        item.hint = hintFor(item, ctx);
        events.push(item);
        tags.forEach((t) => covered.add(t));
    });
    const singles = (Array.isArray(changes) ? changes : []).filter((c) => !covered.has(c.tag)).map((c) => rowItem(c, byTag.get(c.tag), compareBase, ctx));
    return [...events, ...singles].sort((a, b) => (a.rank - b.rank) || (Math.abs(b.usd || 0) - Math.abs(a.usd || 0)));
};

/** Header digest numbers: tags covered, net $, number of partner events. */
export const movedSummary = (items) => ({
    tags: items.reduce((n, it) => n + (it.kind === "event" ? it.n : 1), 0),
    net: items.reduce((s, it) => s + (it.usd || 0), 0),
    events: items.filter((it) => it.kind === "event").length,
});

export const MOVED_LEGEND = "coverage ↓ + traffic ↑ = partner starting to filter · pays ↓ across a partner = repricing · traffic ↓ = our side (Tiger)";

export const MOVED_HELP = [
    "Tags whose yesterday sits outside their usual range but are not alarms (those live in Needs attention).",
    "Partner event = 3 or more tags of one partner moving the same way on the same metric — many small moves of one partner are the story.",
    "Order: filtering signature (coverage down while traffic holds) first, then losses by $, then gains, then chronic moves (4+ days).",
    "$ and % follow the compare control in the Overview (7-day avg or day before).",
    "'usually $22/d → $30' = the tag's 7-day mean vs yesterday. 'day N' = consecutive days out of band.",
];
